import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import type { CampaignReporting } from 'c-entity-types';
import { useEntityData } from 'c-data';
import { Button, CollapsibleCard, FlashMessage, TransparentCard } from 'c-components';
import { Box, CardContent, Stack, TextField, Typography } from '@mui/material';
import { PermissionName } from 'c-sdk';
import { summaryIcon } from 'c-main/icons';
import { useEntityPage } from 'c-hooks';
import { useUserPermissions } from 'c-auth-module/Hooks';
import ReactGA from 'react-ga';
import { useCampaignReportingConfigs } from 'c-main/Components/Campaign/useCampaignReportingConfigs';
import type { SavedSummaryConfig } from 'c-main/Components/Campaign/CampaignAnalytics/SummaryCustomizationDialog';
import SummaryCustomizationDialog from 'c-main/Components/Campaign/CampaignAnalytics/SummaryCustomizationDialog';
import { parseSummaryConfig } from 'c-main/Components/Campaign/CampaignAnalytics/useSummarySources';
import ConfigUserAssign from 'c-admin/Pages/DashboardSetupPage/Components/ConfigUserAssign';

const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
    linear: 'standard (non-DSP) campaigns',
    dsp: 'DSP campaigns',
};

const EditSummaryConfigPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const { id } = useEntityPage();
    const { getById } = useEntityData<CampaignReporting>('CampaignReporting');
    const config = getById({ id });

    const { hasAll } = useUserPermissions();
    const canRename = hasAll([PermissionName.UniledportalCampaign_reportingUpdate]);

    // Renaming and editing both go through the same save the Customize Summary dialog uses, so
    // they can't drift apart on what a valid update looks like.
    const { save, isSaving } = useCampaignReportingConfigs(
        'summary',
        config?.campaign_type ?? undefined,
    );
    const [name, setName] = useState('');
    useEffect(() => {
        if (config?.name) setName(config.name);
    }, [config?.name]);

    const rename = useCallback(() => {
        const trimmed = name.trim();
        if (!trimmed || !config) return;
        save(trimmed, config.config, config.id);
    }, [config, name, save]);

    const canEdit = hasAll([PermissionName.UniledportalCampaign_reportingUpdate]);
    const [saved, setSaved] = useState<'ok' | 'error' | null>(null);

    // The stored config is a JSON string; a malformed one shouldn't take the page down.
    const parsed = useMemo<SavedSummaryConfig | null>(() => {
        if (!config?.config) return null;
        try {
            return parseSummaryConfig(config.config);
        } catch {
            return null;
        }
    }, [config?.config]);

    const saveConfig = useCallback(
        async (next: SavedSummaryConfig) => {
            if (!config) return;
            const ok = await save(config.name, JSON.stringify(next), config.id);
            setSaved(ok ? 'ok' : 'error');
        },
        [config, save],
    );

    const appliesTo = CAMPAIGN_TYPE_LABELS[config?.campaign_type ?? ''] ?? 'every campaign';

    return (
        <TransparentCard
            title={`Summary: ${config?.name ?? id}`}
            titleTypographyProps={{ sx: { fontSize: '1.9rem' } }}
            cardHeaderProps={{ sx: { py: 1 } }}
        >
            <CardContent>
                <Stack gap={3}>
                    {canRename && (
                        <Stack direction="row" gap={2} alignItems="center">
                            <TextField
                                size="small"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                sx={{ minWidth: 260 }}
                            />
                            <Button onClick={rename} disabled={!name.trim() || isSaving}>
                                Rename
                            </Button>
                        </Stack>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        Anyone assigned below can switch to this summary from the switcher under the
                        Summary tab on {appliesTo}, while Summary 2.0 is on. Assignments made
                        against an agency, buyer, brand or media owner reach every user within it.
                    </Typography>

                    <ConfigUserAssign
                        title="Modules.Admin.SummarySetup.assignTitle"
                        configId={id}
                    />

                    {canEdit && (
                        <CollapsibleCard title="Modules.Admin.SummarySetup.editorTitle">
                            <Box sx={{ p: 2 }}>
                                {saved === 'ok' && (
                                    <FlashMessage status="success">Summary saved.</FlashMessage>
                                )}
                                {saved === 'error' && (
                                    <FlashMessage status="error">
                                        Could not save this summary.
                                    </FlashMessage>
                                )}
                                {parsed == null ? (
                                    <FlashMessage status="error">
                                        This saved summary could not be read, so it cannot be edited
                                        here.
                                    </FlashMessage>
                                ) : (
                                    <SummaryCustomizationDialog
                                        inline
                                        value={parsed}
                                        isDsp={config?.campaign_type === 'dsp'}
                                        onSave={saveConfig}
                                    />
                                )}
                            </Box>
                        </CollapsibleCard>
                    )}
                </Stack>
            </CardContent>
        </TransparentCard>
    );
};

const setup: PageEntityConfig<CampaignReporting> = {
    id: 'EditSummaryConfigPage',
    component: EditSummaryConfigPage,
    systemSearch: {
        title: 'Modules.Admin.SummarySetup.editMetaTitle',
        description: 'Modules.Admin.SummarySetup.description',
        type: 'entity',
        icon: summaryIcon,
        route: PostAuthRoutes.Admin.EditSummaryConfig,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Admin.SummarySetup.metaTitle',
                link: PostAuthRoutes.Admin.SummarySetup,
            },
        ],
        defaultIncludes: ['agencies', 'buyers', 'clients', 'owners', 'users'],
        entityName: 'CampaignReporting',
        nameGen: (entity, id) => entity?.name ?? String(id),
        permissions: [
            PermissionName.UniledportalCampaign_reportingRead,
            PermissionName.UniledportalCampaign_reportingAssign,
        ],
        prefix: 'SystemSearch.prefixes.summaryConfig',
        searchColumn: ['name'],
        prefixDescription: 'SystemSearch.prefixDescriptions.summaryConfig',
    },
};

export default setup;
